/**
 * Pre-send compliance check for texts a collector types to a customer. Two things must never go out
 * by SMS: a request for the customer's card details, and a card number itself. Tuned on 120 days of
 * production outbound texts and the saved merchant templates. A FALSE BLOCK HAS NO OVERRIDE, so
 * phrasings that only mention card fields — decline notices, "update your card in the app", "give us
 * a call with the card information" — must stay sendable; the last four of a card on file is exempt.
 */

export type CardComplianceViolation =
  | 'card_request'
  | 'card_number'
  | 'bank_account_request'

// Shown in the block panel after a refused Send.
export const CARD_COMPLIANCE_MESSAGES: Record<CardComplianceViolation, string> =
  {
    card_request:
      'Asking a customer for card details by text puts your payment account at risk of bank fines or immediate suspension. Send a secure payment link instead — the Payment Link button below adds one to your message.',
    card_number:
      'Sending full card numbers by text puts your payment account at risk of bank fines or immediate suspension. Send a secure payment link instead — the Payment Link button below adds one to your message.',
    bank_account_request:
      'Asking a customer for bank account details by text puts your payment account at risk of bank fines or immediate suspension. Send a secure payment link instead — the Payment Link button below adds one to your message.'
  }

// Shown in the hint strip while the draft is being typed, before any Send attempt.
export const CARD_COMPLIANCE_HINTS: Record<CardComplianceViolation, string> = {
  card_request:
    'This looks like a request for card details and will not send. Use Payment Link to collect a card securely.',
  card_number:
    'This message contains a card number and will not send. Use Payment Link to collect a card securely.',
  bank_account_request:
    'This looks like a request for bank account details and will not send. Use Payment Link to collect payment details securely.'
}

const CLAUSE = '[^.!?,;\\n]'
const URL_REGEX = /\bhttps?:\/\/\S+|\b[\w-]+(?:\.[\w-]+)+\/\S*/gi

// Asking for the last few digits of a card or bank account on file is the normal way to confirm
// a stored payment method, and the last four are not sensitive. Removed before the request check.
const LAST_FOUR_REGEX =
  /\blast\s*(?:[2-4]|two|three|four|#)\s*(?:card\s*)?(?:digits?|numbers?|#)?\s*(?:of|on)?\s*(?:the|your|ur|my|that)?\s*(?:new\s+)?(?:(?:bank|checking|savings)\s+)?(?:card|account)(?:\s*(?:number|#|on\s+file|digits))?/gi

// Bank account terms, only ever counted after a request verb: templates explain that autopay "requires
// your account and routing number" without asking for it by text. In this product a bare "account
// number" means the loan account, so it only counts next to routing or checks.
const BANK_TERMS = `(?:(?:bank|checking|savings)\\s+account|ach\\s*(?:inf\\w*|detail\\w*|account)|account\\s*(?:number|num|no\\.|#)?\\s*(?:and|&|\\+|or|/)\\s*routing|routing\\s*(?:number|num|no\\.|#)?\\s*(?:and|&|\\+|/)\\s*account|account\\s*(?:number|num|no\\.|#)(?=${CLAUSE}{0,40}\\bchecks?\\b))`

// Phrases that are a request for card data wherever they appear.
const STRONG_REQUEST_PATTERNS: RegExp[] = [
  /\bfull\s+card\b/i,
  /\bcard\s*#/i,
  /\bcard\s*no\b\s*(?=[#\d])/i,
  /\b(?:your|ur|my|the)\s+cc\b/i,
  /\bcc\s*(?:#|number|num|digits)/i,
  /\bccv\b/i,
  new RegExp(`\\b(?:cvv2?|cvc|security\\s*code)\\b${CLAUSE}{0,15}\\?`, 'i'),
  new RegExp(
    `\\b(?:code|digits?|numbers?)\\b${CLAUSE}{0,12}\\bon\\s+(?:the\\s+)?back\\b`,
    'i'
  ),
  new RegExp(`\\blast\\s*(?:3|three|4|four)\\b${CLAUSE}{0,15}\\bback\\b`, 'i'),
  /\bnumbers?\s+on\s+(?:the\s+|your\s+|ur\s+)?(?:front|back|card)\b/i,
  /\b(?:16|sixteen)[\s-]*digit[\s-]*(?:card\s*)?(?:number|#)/i,
  /\b(?:16|sixteen)[\s-]*digits?\s+on\s+(?:the|your|ur)\s+card\b/i,
  /\b(?:picture|photo|pic|image|screenshot)s?\s+of\s+(?:the\s+)?(?:(?:front|back)\s+(?:and\s+(?:back|front)\s+)?of\s+)?(?:the|your|ur|my)\s+(?:(?:credit|debit)\s+)?card\b/i,
  new RegExp(
    `\\bcard\\b${CLAUSE}{0,20}\\b(?:front\\s+and\\s+back|both\\s+sides)\\b`,
    'i'
  )
]

// Phrases that are a request only when asked for. `exp` covers exp, expiry and expiration.
// `inf\w*` and `detail\w*` absorb the misspellings collectors type (infromation, infomation).
const WEAK_REQUEST_TERMS =
  '(?:card\\s*(?:number|num|no\\.|digits|inf\\w*|detail\\w*)|(?:credit|debit)\\s*card|(?:your|ur|the|my)\\s+card|cvv2?|cvc|ccv|security\\s*code|exp(?:iry|iration)?\\s*(?:date|month|year)?)'
// Verbs that ask for something. Generic verbs (have, need, enter, confirm, update, see) are left
// out on their own, because "you'll need a debit card" and "you can see your card in the app" are
// compliant; they count in the asking form with an I/we subject ("can I see", "let me have").
const REQUEST_VERBS =
  "(?:send|text|give|gimme|provide|share|type|snap|show\\s+(?:me|us)|read\\s+(?:me|us|off|out)|tell\\s+(?:me|us)|reply\\s+with|respond\\s+with|what'?s|what\\s+is|what\\s+are|(?:i|we)\\s+(?:will\\s+)?need|(?:can|could|may)\\s+(?:i|we)\\s+(?:see|have|get|view|take|look\\s+at)|let\\s+(?:me|us)\\s+(?:see|have|get|look\\s+at))"
const ASKED_FOR_PATTERN = new RegExp(
  `\\b${REQUEST_VERBS}\\b(${CLAUSE}{0,40}?)\\b${WEAK_REQUEST_TERMS}\\b`,
  'gi'
)
const ASKED_FOR_BANK_PATTERN = new RegExp(
  `\\b${REQUEST_VERBS}\\b(${CLAUSE}{0,40}?)\\b${BANK_TERMS}`,
  'gi'
)
const NEGATION_BEFORE = new RegExp(
  `\\b(?:not|never|don'?t|do\\s+not|shouldn'?t|won'?t|can'?t|cannot|no)\\b${CLAUSE}{0,30}$`,
  'i'
)
// "give us a call with the card information" asks for a phone call, which is the compliant path.
const REDIRECTED_BETWEEN =
  /\b(?:call|phone|online|link|app|website|log\s*in|login)\b/i
// A number the collector has labelled as something other than a card.
const LABELLED_NUMBER_BEFORE =
  /\b(?:account|acct|loan|order|ref(?:erence)?|invoice|tracking|confirmation|conf|case|ticket|customer|member)\b\s*(?:#|no\.?|number|num|id)?\s*:?\s*[(#]?\s*$/i

// Issuer prefixes and the lengths each brand issues. Every prefix is at most four digits.
const CARD_BRANDS: { prefix: RegExp; lengths: number[] }[] = [
  { prefix: /^4/, lengths: [13, 16, 19] },
  { prefix: /^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/, lengths: [16] },
  { prefix: /^3[47]/, lengths: [15] },
  { prefix: /^(6011|65|64[4-9])/, lengths: [16, 17, 18, 19] },
  { prefix: /^(30[0-5]|3095|36|38|39)/, lengths: [14, 15, 16, 17, 18, 19] },
  { prefix: /^35(2[89]|[3-8]\d)/, lengths: [16, 17, 18, 19] },
  { prefix: /^62/, lengths: [16, 17, 18, 19] },
  {
    prefix: /^(5018|5020|5038|5893|6304|6759|676[1-3])/,
    lengths: [13, 14, 15, 16, 17, 18, 19]
  }
]
const BRAND_PREFIX_LENGTH = 4
const BRAND_FIRST_DIGITS = new Set(['2', '3', '4', '5', '6'])
const PAN_MIN_DIGITS = 13
const PAN_MAX_DIGITS = 19
// Digit groups joined by the separators people type between card groups.
const DIGIT_RUN_REGEX = /\d+(?:[\s.,/()_-]+\d+)*/g

function luhnCheck(digits: string): boolean {
  let total = 0
  let double = false
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let digit = Number(digits[i])
    if (double) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    total += digit
    double = !double
  }

  return digits.length > 0 && total % 10 === 0
}

// Whether some run of consecutive groups starting at `first` forms a card number.
// How many digit groups, starting at `first`, make up a card number: brand prefix, a length that
// brand issues, and a Luhn check. The shortest valid reading wins. 0 when none does.
function cardGroupCount(groups: string[], first: number): number {
  if (!BRAND_FIRST_DIGITS.has(groups[first][0])) return 0

  let digits = ''
  let brand: { lengths: number[] } | undefined
  for (let i = first; i < groups.length; i += 1) {
    digits += groups[i]
    if (digits.length > PAN_MAX_DIGITS) return 0
    if (!brand && digits.length >= BRAND_PREFIX_LENGTH) {
      brand = CARD_BRANDS.find(candidate => candidate.prefix.test(digits))
      if (!brand) return 0
    }
    if (brand && brand.lengths.includes(digits.length) && luhnCheck(digits)) {
      return i - first + 1
    }
  }

  return 0
}

interface CardSpan {
  start: number
  end: number
  digits: string
}

// Every card number in the text, as character spans. `skipLabelled` leaves out a number the
// collector has labelled as an account, loan or order number: right for deciding whether to
// block, wrong for masking, where a Luhn-valid loan number is still a card number to a reader.
function cardSpans(text: string, skipLabelled: boolean): CardSpan[] {
  const spans: CardSpan[] = []
  for (const match of text.matchAll(DIGIT_RUN_REGEX)) {
    const run = match[0]
    const runStart = match.index ?? 0
    if (run.length < PAN_MIN_DIGITS) continue
    if (skipLabelled && LABELLED_NUMBER_BEFORE.test(text.slice(0, runStart)))
      continue
    const groups = Array.from(run.matchAll(/\d+/g), group => ({
      text: group[0],
      offset: group.index ?? 0
    }))
    const groupTexts = groups.map(group => group.text)
    let first = 0
    while (first < groups.length) {
      const used = cardGroupCount(groupTexts, first)
      if (used === 0) {
        first += 1
        continue
      }
      const last = groups[first + used - 1]
      spans.push({
        start: runStart + groups[first].offset,
        end: runStart + last.offset + last.text.length,
        digits: groupTexts.slice(first, first + used).join('')
      })
      first += used
    }
  }

  return spans
}

const CARD_MASK_PREFIX = '**** **** **** '
// A 3-4 digit group this close after a card is taken to be its security code.
const SECURITY_CODE_WINDOW = 60

/**
 * The text with every card number replaced by its last four, and any 3-4 digit group typed
 * shortly after a card blanked out as a probable security code. Unlike the block check, nothing
 * is exempt: a number labelled as a loan number or sitting inside a link is masked too. Same shape as the masking
 * messaging-service applies to inbound texts, so a refused draft can be reported without the
 * number leaving the browser.
 *
 * @param text - The draft
 * @returns The masked draft; text without a card number is returned unchanged
 */
export function maskCardNumbers(text: string): string {
  if (typeof text !== 'string' || text === '') return text
  // Not `prepare`: a card number inside a link, or one labelled as a loan number, still leaves
  // the browser, so it is masked all the same.
  const normalized = text.normalize('NFKC')
  const spans = cardSpans(normalized, false)
  if (spans.length === 0) return text

  let masked = ''
  let cursor = 0
  const maskEnds: number[] = []
  for (const span of spans) {
    masked +=
      normalized.slice(cursor, span.start) +
      CARD_MASK_PREFIX +
      span.digits.slice(-4)
    maskEnds.push(masked.length)
    cursor = span.end
  }
  masked += normalized.slice(cursor)

  // Replacements keep their length, so earlier positions stay valid.
  for (const end of maskEnds) {
    const windowEnd = Math.min(masked.length, end + SECURITY_CODE_WINDOW)
    const blanked = masked
      .slice(end, windowEnd)
      .replace(/\b\d{3,4}\b/g, (group, offset: number) => {
        const before = masked.slice(
          end + offset - CARD_MASK_PREFIX.length,
          end + offset
        )
        // the last four of another masked card, not a security code
        if (before === CARD_MASK_PREFIX) return group

        return '*'.repeat(group.length)
      })
    masked = masked.slice(0, end) + blanked + masked.slice(windowEnd)
  }

  return masked
}

function prepare(text: string): string {
  return text.normalize('NFKC').replace(URL_REGEX, ' ')
}

function isNegated(text: string, index: number): boolean {
  return NEGATION_BEFORE.test(text.slice(Math.max(0, index - 40), index))
}

// A request verb followed by a sensitive term in the same clause, unless the words between them
// redirect the customer elsewhere ("call us with the card information") or the ask is negated.
function asksFor(text: string, askedForPattern: RegExp): boolean {
  for (const match of text.matchAll(askedForPattern)) {
    const between = match[1] ?? ''
    if (REDIRECTED_BETWEEN.test(between)) continue
    if (!isNegated(text, match.index ?? 0)) return true
  }

  return false
}

/**
 * Whether the text contains a card number.
 *
 * @param text - The draft message
 * @returns true when a digit run reads as a card number and is not labelled as something else
 */
export function containsCardNumber(text: string): boolean {
  return cardSpans(prepare(text), true).length > 0
}

/**
 * Whether the text asks the customer for card details.
 *
 * @param text - The draft message
 * @returns true when the text requests a card number, security code, expiry, or a picture of
 *   the card; asking for the last four digits does not count
 */
export function asksForCardDetails(text: string): boolean {
  const prepared = prepare(text).replace(LAST_FOUR_REGEX, ' lastfour ')

  for (const pattern of STRONG_REQUEST_PATTERNS) {
    const match = pattern.exec(prepared)
    if (match && !isNegated(prepared, match.index)) return true
  }

  return asksFor(prepared, ASKED_FOR_PATTERN)
}

/**
 * Whether the text asks the customer for bank account details. The routing number alone is
 * public and does not count; a bare "account number" is the loan account and does not count.
 *
 * @param text - The draft message
 * @returns true when the text requests a bank, checking or savings account number, or account
 *   and routing details together
 */
export function asksForBankAccountDetails(text: string): boolean {
  const prepared = prepare(text).replace(LAST_FOUR_REGEX, ' lastfour ')

  return asksFor(prepared, ASKED_FOR_BANK_PATTERN)
}

/**
 * The compliance violation in a draft message, if any. A card number outranks a card request,
 * since the number is the more serious thing to stop, and card requests outrank bank requests.
 *
 * @param text - The draft message
 * @returns The violation, or null when the message may be sent
 */
export function checkCardCompliance(
  text: string
): CardComplianceViolation | null {
  if (typeof text !== 'string' || text.length === 0) return null
  if (containsCardNumber(text)) return 'card_number'
  if (asksForCardDetails(text)) return 'card_request'
  if (asksForBankAccountDetails(text)) return 'bank_account_request'

  return null
}
